import React, { ComponentProps } from 'react'
import { Spin } from 'antd'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import { useCalculationTimepointCountValues } from '../../../../api/benchmarking'
import BenchmarkingContentRow from '../../components/BenchmarkingContentRow'
import { BenchmarkingLegend } from '../../components/BenchmarkingLegend'
import { baseGraphOptions } from '../../util/baseGraphOptions'
import { getColorByIndex } from '../../util/colors'
import { getFontSize } from '../../util/fontSize'
import { BORDER_WIDTH } from '../../util/constants'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

const TITLE = 'Antal tidpunkter per beräkning'

export const CalculationTimepointCount: React.FC<{ text: React.ReactNode }> = ({ text }) => {
  const { data, isLoading, error } = useCalculationTimepointCountValues()

  if (isLoading) {
    return <BenchmarkingContentRow graph={<Spin size="large" />} text={text} title={TITLE} />
  }

  if (error || !data) {
    return (
      <BenchmarkingContentRow
        title={TITLE}
        text={text}
        graph={
          <div className="flex h-full w-full items-center justify-center font-mono text-sm text-red-600">
            <span>{error ? `Fel vid hämtning av data: ${String(error)}` : 'Ingen data tillgänglig'}</span>
          </div>
        }
      />
    )
  }

  const municipalities = Array.from(new Set(data.map((r) => r.municipality_name as string))).sort()

  // Samla alla unika labels, sorterade efter timepoint_count (numeriskt stigande)
  const labelsSorted = Array.from(
    new Map(data.map((r) => [Number(r.timepoint_count), r.label as string])).entries()
  )
    .sort(([a], [b]) => a - b)
    .map(([, label]) => label)

  const datasets = labelsSorted.map((label, i) => {
    const color = getColorByIndex(i)
    return {
      label,
      backgroundColor: color.fill,
      borderColor: color.border,
      borderWidth: BORDER_WIDTH,
      data: municipalities.map((name) => {
        const row = data.find((r) => r.municipality_name === name && r.label === label)
        return row ? Number(row.proportion_pct) : 0
      }),
    }
  })

  const options: ComponentProps<typeof Bar>['options'] = {
    ...baseGraphOptions,
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y}%`,
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        ticks: { font: { size: getFontSize('md') } },
      },
      y: {
        stacked: true,
        min: 0,
        max: 100,
        ticks: { font: { size: getFontSize('md') }, callback: (v) => `${v}%` },
        title: { display: true, text: 'Andel beräkningar (%)', font: { size: getFontSize('md') } },
      },
    },
  }

  const legend = (
    <BenchmarkingLegend
      items={labelsSorted.map((label, i) => {
        const color = getColorByIndex(i)
        return { text: label, fillColor: color.fill, borderColor: color.border }
      })}
    />
  )

  return <BenchmarkingContentRow title={TITLE} text={text} legend={legend} graph={<Bar data={{ labels: municipalities, datasets }} options={options} />} />
}
